/**
 * Leave/PTO API Client
 *
 * Handles all leave policy, balance, request, and approval operations
 */
import { config } from '../config';
import { useAuthStore } from '../../stores/auth.store';
const LEAVE_API_URL = config.api.leave;
/**
 * Get authorization headers with JWT token from auth store
 */
function getAuthHeaders() {
    const { tokens, user } = useAuthStore.getState();
    const token = tokens?.idToken;
    const tenantId = user?.tenantId || 'tenant-001';
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
    };
}
/**
 * Handle API errors
 */
async function handleResponse(response) {
    if (!response.ok) {
        // For 404s, return a more specific error that can be detected by retry logic
        if (response.status === 404) {
            throw new Error(`HTTP 404: ${response.url}`);
        }
        // For 403s, return a more specific error with the actual message
        if (response.status === 403) {
            const error = await response.json().catch(() => ({ message: 'Forbidden' }));
            throw new Error(`HTTP 403: ${error.message || 'Forbidden'}`);
        }
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
}
// ============================================================================
// Policy Management Operations (Admin)
// ============================================================================
/**
 * Create a new leave policy (admin only)
 */
export async function createPolicy(request) {
    const response = await fetch(`${LEAVE_API_URL}/policies`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * List all leave policies with optional filtering
 */
export async function listPolicies(request) {
    const params = new URLSearchParams();
    if (request?.status) {
        params.append('status', request.status);
    }
    if (request?.leaveType) {
        params.append('leaveType', request.leaveType);
    }
    const queryString = params.toString();
    const url = queryString ? `${LEAVE_API_URL}/policies?${queryString}` : `${LEAVE_API_URL}/policies`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get detailed information about a specific policy
 */
export async function getPolicy(policyId) {
    const response = await fetch(`${LEAVE_API_URL}/policies/${policyId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Update an existing leave policy (admin only)
 */
export async function updatePolicy(policyId, request) {
    const response = await fetch(`${LEAVE_API_URL}/policies/${policyId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * Assign a policy to an employee (admin only)
 */
export async function assignPolicy(employeeId, request) {
    const response = await fetch(`${LEAVE_API_URL}/employees/${employeeId}/assign-policy`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
// ============================================================================
// Leave Request Operations (Employee)
// ============================================================================
/**
 * Create a new leave request (employee)
 */
export async function createLeaveRequest(request) {
    const response = await fetch(`${LEAVE_API_URL}/requests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * List leave requests with role-based filtering
 * - Employee: sees own requests
 * - Manager: sees direct reports (requires employeeId)
 * - Admin: sees all (requires employeeId)
 */
export async function listLeaveRequests(request) {
    const params = new URLSearchParams();
    if (request?.employeeId) {
        params.append('employeeId', request.employeeId);
    }
    if (request?.status) {
        params.append('status', request.status);
    }
    if (request?.startDate) {
        params.append('startDate', request.startDate);
    }
    if (request?.endDate) {
        params.append('endDate', request.endDate);
    }
    const queryString = params.toString();
    const url = queryString ? `${LEAVE_API_URL}/requests?${queryString}` : `${LEAVE_API_URL}/requests`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
// ============================================================================
// Manager Approval Operations
// ============================================================================
/**
 * Get manager's approval inbox (pending requests from direct reports)
 */
export async function getApprovalInbox(request) {
    const params = new URLSearchParams();
    if (request?.status) {
        params.append('status', request.status);
    }
    const queryString = params.toString();
    const url = queryString
        ? `${LEAVE_API_URL}/approvals/inbox?${queryString}`
        : `${LEAVE_API_URL}/approvals/inbox`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Review a leave request (approve or reject) - manager only
 */
export async function reviewLeaveRequest(requestId, employeeId, request) {
    const response = await fetch(`${LEAVE_API_URL}/requests/${requestId}/review?employeeId=${employeeId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * Cancel a leave request - employee only (own pending requests)
 */
export async function cancelLeaveRequest(requestId) {
    const response = await fetch(`${LEAVE_API_URL}/requests/${requestId}/cancel`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
// ============================================================================
// Balance Management Operations
// ============================================================================
/**
 * Get employee's leave balance for a specific leave type
 */
export async function getBalance(employeeId, leaveType) {
    const response = await fetch(`${LEAVE_API_URL}/employees/${employeeId}/balances/${leaveType}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get all leave balances for an employee
 * If employeeId is not provided, fetches balances for the current user
 */
export async function getBalances(employeeId) {
    const path = employeeId ? `/employees/${employeeId}/balances` : '/my/balances';
    const response = await fetch(`${LEAVE_API_URL}${path}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Manually adjust employee's leave balance (admin only)
 */
export async function adjustBalance(employeeId, leaveType, request) {
    const response = await fetch(`${LEAVE_API_URL}/employees/${employeeId}/balances/${leaveType}/adjust`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
// ============================================================================
// Ledger Operations (Read-only)
// ============================================================================
/**
 * Get ledger history for an employee's leave type
 * Shows all accruals, usages, adjustments, carryovers
 */
export async function getLedgerHistory(employeeId, leaveType, startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) {
        params.append('startDate', startDate);
    }
    if (endDate) {
        params.append('endDate', endDate);
    }
    const queryString = params.toString();
    const url = queryString
        ? `${LEAVE_API_URL}/employees/${employeeId}/ledger/${leaveType}?${queryString}`
        : `${LEAVE_API_URL}/employees/${employeeId}/ledger/${leaveType}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
// ============================================================================
// Policy Assignment Operations
// ============================================================================
/**
 * Get an employee's policy assignments
 */
export async function getEmployeePolicyAssignments(employeeId) {
    const response = await fetch(`${LEAVE_API_URL}/employees/${employeeId}/policies`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get all employees assigned to a specific policy (admin only)
 */
export async function getPolicyAssignments(policyId) {
    const response = await fetch(`${LEAVE_API_URL}/policies/${policyId}/assignments`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
